import { z } from 'zod';
import { ACCESS_RULES } from '../domain/access.js';
import { DECK_DRAW_MODES, DECK_KINDS, DECK_STATUSES } from '../domain/deck.js';
import { TRAINING_BLOCK_TYPES, TRAINING_STATUSES } from '../domain/training.js';
import { isHttpUrl, isOwnPath } from './url.js';

/** Deck and training-material administration (§18, §45, §48). */

const title = z.string().trim().min(2, 'Название должно быть не короче 2 символов').max(120);
const description = z.string().trim().max(1000).nullable().optional();

export const createDeckSchema = z.object({
  title,
  description,
  kind: z.enum(DECK_KINDS).default('generic'),
});
export type CreateDeckInput = z.infer<typeof createDeckSchema>;

export const updateDeckSchema = z.object({
  title: title.optional(),
  description,
  status: z.enum(DECK_STATUSES).optional(),
  /** §36 — per-deck draw behaviour stays configurable, not hard-coded. */
  drawMode: z.enum(DECK_DRAW_MODES).optional(),
  /** §18.8 — whether the deck appears on the free-form table. */
  availableInFreeform: z.boolean().optional(),
  coverImage: z.string().max(500).nullable().optional(),
  cardBackImage: z.string().max(500).nullable().optional(),
});
export type UpdateDeckInput = z.infer<typeof updateDeckSchema>;

export const reorderCardsSchema = z.object({
  cardIds: z.array(z.string().min(1)).min(1).refine(
    (ids) => new Set(ids).size === ids.length,
    'Идентификаторы карт не должны повторяться',
  ),
});
export type ReorderCardsInput = z.infer<typeof reorderCardsSchema>;

export const updateCardSchema = z.object({
  title: z.string().trim().max(200).nullable().optional(),
});
export type UpdateCardInput = z.infer<typeof updateCardSchema>;

/**
 * Access configuration is declarative: the admin states which rules open an
 * item, and the resolver applies them (§10). Kept as one call because the rules
 * of an item only make sense as a set.
 */
export const setAccessRulesSchema = z.object({
  rules: z.array(z.enum(ACCESS_RULES)),
  /** Which game must be owned when `game_owner` is among the rules. */
  requiredGameId: z.string().min(1).nullable().optional(),
});
export type SetAccessRulesInput = z.infer<typeof setAccessRulesSchema>;

/* -------------------------------------------------------------------------- */
/* Training                                                                   */
/* -------------------------------------------------------------------------- */

export const createTrainingMaterialSchema = z.object({
  title,
  description,
});
export type CreateTrainingMaterialInput = z.infer<typeof createTrainingMaterialSchema>;

export const updateTrainingMaterialSchema = z.object({
  title: title.optional(),
  description,
  status: z.enum(TRAINING_STATUSES).optional(),
  coverImage: z.string().max(500).nullable().optional(),
});
export type UpdateTrainingMaterialInput = z.infer<typeof updateTrainingMaterialSchema>;

/**
 * Ссылка в блоке обучения: видео, PDF или внешний материал.
 *
 * Схема проверяется явно. React 19 сейчас гасит `javascript:`-ссылки сам, а
 * переход на `data:` верхнего уровня запрещают браузеры, — но полагаться на
 * фреймворк в вопросе, что попадёт в href, значит держать защиту не на своём
 * уровне. Поле заполняет администратор, и ограничение стоит там же, где
 * остальные правила ввода.
 */
const blockUrl = z
  .string()
  .trim()
  .max(1000)
  .refine(
    (value) => {
      if (value === '') return true;
      /*
        Свой файл на этой же площадке — тоже адрес: загруженная картинка лежит
        по `/uploads/...`, подготовленная графика по `/assets/...`. Раньше такие
        пути отвергались, и поле требовало абсолютную ссылку, тогда как
        собственная подсказка под ним советовала написать `/assets/...`.
      */
      return isOwnPath(value) || isHttpUrl(value);
    },
    'Ссылка должна начинаться с http://, https:// или с «/» — как у файла на этой площадке',
  )
  .nullable()
  .optional();

export const createTrainingBlockSchema = z.object({
  type: z.enum(TRAINING_BLOCK_TYPES),
  title: z.string().trim().max(200).nullable().optional(),
  body: z.string().max(20_000).nullable().optional(),
  url: blockUrl,
});
export type CreateTrainingBlockInput = z.infer<typeof createTrainingBlockSchema>;

export const updateTrainingBlockSchema = z.object({
  title: z.string().trim().max(200).nullable().optional(),
  body: z.string().max(20_000).nullable().optional(),
  url: blockUrl,
});
export type UpdateTrainingBlockInput = z.infer<typeof updateTrainingBlockSchema>;

export const reorderTrainingBlocksSchema = z.object({
  blockIds: z.array(z.string().min(1)).min(1).refine(
    (ids) => new Set(ids).size === ids.length,
    'Идентификаторы блоков не должны повторяться',
  ),
});
export type ReorderTrainingBlocksInput = z.infer<typeof reorderTrainingBlocksSchema>;
